import {Component, Inject} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {NameValueDTO} from '../../models/NameValueDTO';

interface ConcessionFormGroup {
  enabled: boolean;
  dateRange: {
    start: Date | null;
    end: Date | null;
  } | null;
}

export interface UnderwritingConcession {
  underwritingType: NameValueDTO;
  startDate: Date | number | null;
  endDate: Date | number | null;
}
export interface UnderwritingConcessionDialogData {
  underwritingTypes: NameValueDTO[];
  underwritingConcessionsToEdit: UnderwritingConcession[];
  underwritingConcessionsLatestPerType?: UnderwritingConcession[];
}

@Component({
  selector: 'greydom-underwriting-concessions-dialog',
  templateUrl: './underwriting-concession-dialog.component.html',
  styleUrls: ['../../../../../styles/global/_dialog-styles.scss']
})
export class UnderwritingConcessionDialogComponent {
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<UnderwritingConcessionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UnderwritingConcessionDialogData
  ) {
    if (data.underwritingTypes?.length) {
      this.initializeForm(data.underwritingTypes);
      if (data.underwritingConcessionsToEdit?.length) {
        this.updateFormWithExistingConcessions(data.underwritingConcessionsToEdit);
      }
    }
  }

  private initializeForm(types: NameValueDTO[]): void {
    const group = types.reduce((acc, type) => ({
      ...acc,
      [type.value]: this.fb.group({
        enabled: [false],
        dateRange: [null]
      })
    }), {});

    this.form = this.fb.group(group);
  }

  onToggleConcession(enabled: boolean, typeValue: number): void {
    const control = this.form.get(typeValue.toString());
    if (control && !enabled) {
      control.patchValue({ dateRange: null });
    }
  }

  private updateFormWithExistingConcessions(concessions: UnderwritingConcession[]): void {
    concessions.forEach(concession => {
      if (concession.startDate || concession.endDate) {
        const control = this.form.get(concession.underwritingType.value.toString());

        if (control) {
          control.patchValue({
            enabled: true,
            dateRange: {
              start: concession.startDate ? new Date(concession.startDate) : null,
              end: concession.endDate ? new Date(concession.endDate) : null
            }
          });
        }
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.valid) {
      const formValue = this.form.value as { [key: string]: ConcessionFormGroup };

      const result = Object.entries(formValue)
        .filter(([_, group]) => group.enabled && group.dateRange)
        .map(([typeValue, group]) => {
          const type = this.data.underwritingTypes.find(t => t.value === Number(typeValue));
          return {
            underwritingType: {
              value: Number(typeValue),
              name: type?.name || ''
            },
            startDate: group.dateRange?.start ? new Date(group.dateRange.start).getTime() : null,
            endDate: group.dateRange?.end ? new Date(group.dateRange.end).getTime() : null
          };
        });

      this.dialogRef.close(result);
    }
  }

  isSaveDisabled(): boolean {
    const formValue = this.form.value;
    return Object.entries(formValue).some(([_, group]: [string, ConcessionFormGroup]) =>
      group.enabled && (!group.dateRange || !group.dateRange.start || !group.dateRange.end)
    );
  }

  getMinimumStartDatePerType(typeValue: number): Date | null {
    if (!this.data.underwritingConcessionsLatestPerType || this.data.underwritingConcessionsLatestPerType.length === 0) {
      return null;
    }
    const latestConcession = this.data.underwritingConcessionsLatestPerType.find(c => c.underwritingType.value === typeValue);
    if (latestConcession && latestConcession.endDate) {
      const date = new Date(latestConcession.endDate);
      date.setDate(date.getDate() + 1);
      return date;
    }
    return null;
  }
}
